// 调用真实大模型，把用户描述生成为可视化拓扑图数据。
// 取代原先的本地固定 mock 返回。

import { complete } from './llmClient.js'
import { buildSystemPrompt, buildUserPrompt } from './prompt.js'
import { withStyle } from './topology.js'

// 从模型输出中提取 JSON 对象：容忍 ```json 代码块或前后夹带的文字。
function extractJson(text) {
  const trimmed = String(text || '').trim()
  // 优先剥离 Markdown 代码块围栏。
  const fenced = trimmed.match(/```(?:json)?\s*([\s\S]*?)```/i)
  const candidate = fenced ? fenced[1].trim() : trimmed
  try {
    return JSON.parse(candidate)
  } catch (e) {
    void e
  }
  // 退化处理：截取第一个 { 到最后一个 } 之间的内容再解析。
  const start = candidate.indexOf('{')
  const end = candidate.lastIndexOf('}')
  if (start !== -1 && end > start) {
    return JSON.parse(candidate.slice(start, end + 1))
  }
  throw new Error('无法从大模型输出中解析出拓扑 JSON。')
}

// 校验并规整图数据：只保留两端存在的边，节点补齐样式。
// 接口/网卡不是设备节点，绝不能出现在 nodes 里（真实节点是 N…/AP…/UE… 等，
// 不含 :IF、也不以 IF/WIF 开头）。大模型偶尔会把接口误当节点，导致图上多出
// 一个无边的孤立节点（被 fitView 甩到角落）。这里防御性剔除。
const INTERFACE_ID_RE = /(:W?IF)|(^W?IF\d)/i

function normalizeGraph(raw) {
  const rawNodes = Array.isArray(raw.nodes) ? raw.nodes : []
  const edges = Array.isArray(raw.edges) ? raw.edges : []

  const nodes = rawNodes.filter((n) => !INTERFACE_ID_RE.test(String(n.id || '')))
  const dropped = rawNodes.length - nodes.length
  if (dropped > 0) {
    // eslint-disable-next-line no-console
    console.warn(`[topology] 已剔除 ${dropped} 个疑似接口/网卡的伪节点`)
  }
  if (!nodes.length) throw new Error('大模型返回的拓扑没有任何节点。')

  const nodeIds = new Set(nodes.map((n) => String(n.id)))
  const validEdges = edges.filter(
    (e) => nodeIds.has(String(e.source)) && nodeIds.has(String(e.target))
  )

  // 剔除无任何连接的孤立节点（LLM 偶尔多产出、会被 fitView 甩到角落）。
  // 仅当图里存在边时才剔除，避免误删"纯节点"拓扑。
  let finalNodes = nodes
  if (validEdges.length > 0) {
    const connected = new Set()
    validEdges.forEach((e) => {
      connected.add(String(e.source))
      connected.add(String(e.target))
    })
    const before = finalNodes.length
    finalNodes = finalNodes.filter((n) => connected.has(String(n.id)))
    const isolated = before - finalNodes.length
    if (isolated > 0) {
      // eslint-disable-next-line no-console
      console.warn(`[topology] 已剔除 ${isolated} 个无连接的孤立节点`)
    }
  }

  return { nodes: withStyle(finalNodes), edges: validEdges }
}

/**
 * 请求大模型生成拓扑图数据。
 * @param {{text: string, type: string}} payload 用户描述与拓扑类型
 * @returns {Promise<{nodes: Array, edges: Array}>}
 */
export async function requestLLM({ text, type }) {
  const messages = [
    { role: 'system', content: buildSystemPrompt() },
    { role: 'user', content: buildUserPrompt({ text, type }) }
  ]
  const output = await complete(messages)
  return normalizeGraph(extractJson(output))
}
